import { useEffect, useState } from 'react'
import { Loader2 } from '@/lib/icons'
import { Button } from '@/components/ui/button'
import { Modal } from '@/components/ui/modal'
import { validateNewAdminName, validateNewAdminTelegramId } from './validation'

export interface AddAdminModalProps {
  open: boolean
  isSubmitting: boolean
  errorMessage: string | null
  onCancel: () => void
  onSubmit: (telegramId: number, name: string) => void
}

/**
 * "+ Admin qo'shish" oynasi — `docs/03-app-flow.md` "Sahifa 6". `AdminCreateRequest`
 * (`packages/shared/src/api.ts`) FIKS shakl: `telegramId` + `name`. Server yangi adminni
 * har doim `isSuper: false` bilan yaratadi (`services/admins.ts` izohi) — bu forma super
 * huquq berish uchun EMAS, faqat oddiy admin qo'shish uchun, shuning uchun bu yerda
 * "super" tanlovi umuman yo'q.
 */
export function AddAdminModal({
  open,
  isSubmitting,
  errorMessage,
  onCancel,
  onSubmit,
}: AddAdminModalProps) {
  const [telegramIdRaw, setTelegramIdRaw] = useState('')
  const [name, setName] = useState('')

  // Har safar YANGI ochilganda oldingi forma holati qolib ketmasin.
  useEffect(() => {
    if (open) {
      setTelegramIdRaw('')
      setName('')
    }
  }, [open])

  const telegramIdField = validateNewAdminTelegramId(telegramIdRaw)
  const nameError = validateNewAdminName(name)
  const canSubmit = telegramIdField.value !== null && nameError === null && !isSubmitting

  function handleSubmit() {
    if (telegramIdField.value === null || nameError !== null) return
    onSubmit(telegramIdField.value, name.trim())
  }

  return (
    <Modal open={open} onClose={onCancel} ariaLabel="Admin qo'shish" size="compact" zIndex={50}>
      <div className="flex flex-col gap-4 p-5">
        <h2 className="text-base font-semibold">Admin qo&apos;shish</h2>

        <div className="flex flex-col gap-1">
          <label htmlFor="new-admin-telegram-id" className="text-xs font-medium text-app-hint">
            Telegram ID
          </label>
          <input
            id="new-admin-telegram-id"
            type="text"
            inputMode="numeric"
            autoComplete="off"
            value={telegramIdRaw}
            onChange={(event) => setTelegramIdRaw(event.target.value)}
            disabled={isSubmitting}
            autoFocus
            placeholder="Masalan: 123456789"
            className="tabular h-11 w-full rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
          />
          {telegramIdField.error && (
            <p className="text-xs text-stage-rejected-text">{telegramIdField.error}</p>
          )}
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="new-admin-name" className="text-xs font-medium text-app-hint">
            Ism
          </label>
          <input
            id="new-admin-name"
            type="text"
            autoComplete="off"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSubmitting}
            placeholder="Masalan: Aziz"
            className="h-11 w-full rounded-input border border-app-border bg-app-bg px-3 text-base text-app-text outline-none focus-visible:border-brand disabled:opacity-60"
          />
          {nameError && <p className="text-xs text-stage-rejected-text">{nameError}</p>}
        </div>

        {errorMessage && (
          <p className="text-xs font-medium text-stage-rejected-text">{errorMessage}</p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onCancel} disabled={isSubmitting}>
            Bekor qilish
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit}>
            {isSubmitting && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            Qo&apos;shish
          </Button>
        </div>
      </div>
    </Modal>
  )
}
